import { Icon } from "armstrong-react";
import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router";
import { Dispatch } from "redux";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { Avatar, FlowBox, LinkButton, Loader } from "../../../core/atoms/atoms";
import { CardContainer } from "../../../shared/components/inspiration/cardContainer";
import { FlexiCard, GetInspiredCard, UpsellCard } from "../../../shared/components/inspiration/cards";
import { ContentCard, LocationCard } from "@travellocal/ui";
import { apiPromiseHook, createEnterHookPromise } from "../../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../../shared/helpers/contentCollectionHelpers";
import { MatchState } from "../../../shared/helpers/reduxHelpers";
import ResponsiveContainer from "../../../shared/helpers/responsiveContainer";
import * as StringUtil from "../../../shared/helpers/stringHelpers";
import { SwipeCarousel } from "../../components/swipeCarousel/swipeCarousel";
import { RoutesInvoker } from "../../routesInvoker";
import { ActionPromises } from "../../state/actionPromises";

const stateToProps = (state: IWebStoreState) => ({
  ltoState: state.lto,
  contentState: state.contentByCollection,
});

const dispatchToProps = (dispatch: Dispatch<IWebStoreState>) => ({
  dispatch,
});

type Props = ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps>;

export const LtoDetailsComponent = (props: Props) => {
  const { ltoState, contentState } = props;
  const columnClasses = "column is-6-tablet is-4-desktop is-3-widescreen";

  if (ltoState.status !== "OK") {
    return <Loader />;
  }

  const lto = ltoState.data;
  const ltoImage = lto.image ? lto.image.thumbnailUrl : "/images/missing-image.jpg";
  const ltoCountries = ltoState.data.locations.filter((l) => l.locationType === LocationType.Country && l.locationStatus === LocationStatus.Live);
  const ltoDisplayName = lto.publicName || lto.name;
  const ltoDisplaySpeciality = lto.publicSpeciality || lto.speciality;
  const ltoDisplayDescription = lto.publicDescription || lto.description;

  const countriesSentence = StringUtil.arrayToSentence(ltoCountries.map((x) => x.name));

  const enquireRouteParams = {
    locationSlug: ltoCountries.length > 0 ? ltoCountries[0].slug : null,
  };

  const EnquiryUpsellCard = () => (
    <FlexiCard
      to={WebRoutes.enquiryForm({ ...enquireRouteParams, campaign: "lto-trip-ideas" })}
      className="send-enquiry cta-card"
      icomoon={Icon.Icomoon.paperplane}
      title="Something in mind?"
      description={`Every trip created on TravelLocal is unique. If you already have an idea of your dream holiday,
                      ${ltoDisplayName} will be happy to help - just send an enquiry!`}
      cta="Customise your trip"
    />
  );

  const baseContentFilters = {
    locations: lto.locations.map((x) => x.id),
  };
  const ltoReviewsState = contentState.data[CollectionKeyHelper.LtoReviews(lto.id)];

  return (
    <div className="lto-details">
      <FlowBox className="details-body marketing-2cols-sidebar width-wrapper">
        <div className="profile">
          <Avatar source={ltoImage} size={Avatar.UISizes.xlarge} />
          <p className="specialty">{ltoDisplaySpeciality}</p>
          <p>{ltoDisplayDescription}</p>
          <p><Link to={WebRoutes.ltoPtr(lto.slug)}>Package Travel and Linked Travel Arrangements Regulations</Link></p>
        </div>

        <div className="sidebar">
          <div className="locations">
            <h4>We offer tailor-made holidays in:</h4>
            {ltoCountries.map((location) => (
              <LocationCard key={`loc-${location.id}`} location={location} fallbackUrl={ltoImage} />
            ))}
          </div>
          <hr />

          {MatchState(ltoReviewsState, {
            ok: () => {
              const featuredReview = ltoReviewsState.data[0];

              if (featuredReview != null) {
                return (
                  <div>
                    <ContentCard
                      content={featuredReview}
                      filterContext={{ locations: ltoCountries.map(x => x.id) }}
                    />
                  </div>
                );
              }
              else {
                return <UpsellCard />;
              }
            },
          })}
        </div>
      </FlowBox>

      <div className="main-cta">
        <LinkButton to={WebRoutes.enquiryForm({ ...enquireRouteParams, campaign: "lto-main" })} icomoon={Icon.Icomoon.paperplane}>
          Let's create your trip!
        </LinkButton>
      </div>

      <section className="card-section">
        <div className="width-wrapper">
          <h2>Trip ideas by {ltoDisplayName}</h2>
          <p className="note">We asked {ltoDisplayName} to put together some example itineraries to showcase {countriesSentence}. Take a look and get inspired!</p>

          <ResponsiveContainer
            mobile={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.LtoItineraries(lto.id)}
                filters={{ ...baseContentFilters, types: [ContentType.SampleItinerary] }}
                renderCardWrapper={(elements) => (
                  <SwipeCarousel>
                    {elements}
                    <EnquiryUpsellCard />
                  </SwipeCarousel>
                )}
              />
            )}
            tablet={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.LtoItineraries(lto.id)}
                filters={{ ...baseContentFilters, types: [ContentType.SampleItinerary] }}
                columnClassName={columnClasses}
              >
                <EnquiryUpsellCard />
              </CardContainer>
            )}
          />
        </div>
      </section>

      {MatchState(ltoReviewsState, {
        ok: () => (ltoReviewsState.data && ltoReviewsState.data.length) && (
          <section className="card-section">
            <div className="width-wrapper">
              <h2>Reviews for {ltoDisplayName}</h2>

              <ResponsiveContainer
                mobile={() => (
                  <CardContainer
                    collectionKey={CollectionKeyHelper.LtoReviews(lto.id)}
                    filters={{ ...baseContentFilters, types: [ContentType.Review] }}
                    renderCardWrapper={(elements) => (
                      <SwipeCarousel>
                        {elements}
                      </SwipeCarousel>
                    )}
                  />
                )}
                tablet={() => (
                  <CardContainer
                    collectionKey={CollectionKeyHelper.LtoReviews(lto.id)}
                    filters={{ ...baseContentFilters, types: [ContentType.Review] }}
                    columnClassName={columnClasses}
                  />
                )}
              />
            </div>
          </section>
        ),
      })}

      <section className="card-section">
        <div className="width-wrapper">
          <h2>Need some inspiration?</h2>
          <p className="note">Our collection of articles about {countriesSentence} will help!</p>

          <ResponsiveContainer
            mobile={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.LtoOverviewRelated(lto.id)}
                filters={{ ...baseContentFilters, types: [ContentType.BlogPost] }}
                renderCardWrapper={(elements) => (
                  <SwipeCarousel>
                    {elements}
                    <GetInspiredCard filterSet={baseContentFilters} />
                  </SwipeCarousel>
                )}
              />
            )}
            tablet={() => (
              <CardContainer
                collectionKey={CollectionKeyHelper.LtoOverviewRelated(lto.id)}
                filters={{ ...baseContentFilters, types: [ContentType.BlogPost] }}
                columnClassName={columnClasses}>
                <GetInspiredCard filterSet={baseContentFilters} />
              </CardContainer>
            )}
          />
        </div>
      </section>
    </div>
  );
}

export const LtoDetails = connect(stateToProps, dispatchToProps)(LtoDetailsComponent);
export const ltoDetailsOnEnter = apiPromiseHook(
  createEnterHookPromise((route) => ActionPromises.initialiseLtoDetail(route.params.ltoSlug)),
);
